import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../../services/query";
import { Operation } from "../../../../models";

import { useOperationQuery } from "./operation.query";

export * from "./operation.invalidate";

export const getOperationAccessError = (
  operationId: string | undefined,
): Error =>
  new Error(
    `Attempted to access operation data for given operationId "${operationId}" but it is not available`,
  );

// ########################
// ## useOperationStatus ##
// ########################

export type ResultOfUseOperationStatus = QueryStatus;
export type UseOperationStatus = (
  operationId: string | undefined,
) => ResultOfUseOperationStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the operation data of the given operationId.
 */
export const useOperationStatus: UseOperationStatus = (operationId) => {
  return useOperationQuery({ operationId, watch: ["status"] }).status;
};

// #######################
// ## useOperationError ##
// #######################

export type ResultOfUseOperationError = AxiosError | null;
export type UseOperationError = (
  operationId: string | undefined,
) => ResultOfUseOperationError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the operation data
 * of the given operationId or null if no error occurred.
 */
export const useOperationError: UseOperationError = (operationId) => {
  return useOperationQuery({ operationId, watch: ["error"] }).error;
};

// ###########################
// ## useMaybeOperationData ##
// ###########################

export type ParamsOfUseMaybeOperationData = {
  operationId: string | undefined;
};
export type ResultOfUseMaybeOperationData = Operation | undefined;
export type UseMaybeOperationData = (
  params: ParamsOfUseMaybeOperationData,
) => ResultOfUseMaybeOperationData;

/**
 * The returned value of this hook will contain
 * the operation data of the given operationId.
 *
 * This data may be undefined.
 */
export const useMaybeOperationData: UseMaybeOperationData = ({
  operationId,
}) => {
  return useOperationQuery({ operationId, watch: ["data"] }).data;
};

// ######################
// ## useOperationData ##
// ######################

export type ParamsOfUseOperationData = {
  operationId: string | undefined;
};
export type ResultOfUseOperationData = Operation;
export type UseOperationData = (
  params: ParamsOfUseOperationData,
) => ResultOfUseOperationData;

/**
 * The returned value of this hook will contain
 * the operation data of the given operationId.
 *
 * This hook is guaranteed to contain the operation data
 * of the given operationId or will throw.
 */
export const useOperationData: UseOperationData = ({ operationId }) => {
  const data = useMaybeOperationData({ operationId });
  if (data === undefined) {
    throw getOperationAccessError(operationId);
  }
  return data;
};

// #######################
// ## useMaybeOperation ##
// #######################

export type ParamsOfUseMaybeOperation = {
  operationId: string | undefined;
};
export type ResultOfUseMaybeOperation = QueryEnvelope<Operation>;
export type UseMaybeOperation = (
  params: ParamsOfUseMaybeOperation,
) => ResultOfUseMaybeOperation;

/**
 * The `data` property returned from this hook will contain
 * the operation data of the given operationId.
 *
 * This data may be undefined.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the operation data of the given operationId.
 */
export const useMaybeOperation: UseMaybeOperation = ({ operationId }) => {
  const { status, error, data } = useOperationQuery({
    operationId,
    watch: ["status", "error", "data"],
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ##################
// ## useOperation ##
// ##################

export type ParamsOfUseOperation = {
  operationId: string | undefined;
};
export type ResultOfUseOperation = EnsuredQueryEnvelope<Operation>;
export type UseOperation = (
  params: ParamsOfUseOperation,
) => ResultOfUseOperation;

/**
 * The `data` property returned from this hook will contain
 * the operation data of the given operationId.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the operation data of the given operationId.
 *
 * This hook is guaranteed to contain operation data
 * of the given operationId or will throw.
 *
 * NOTE: This is ONLY EVER TO BE USED in `useCurrentOperation`!
 */
export const useOperation: UseOperation = ({ operationId }) => {
  const { status, error, data } = useOperationQuery({
    operationId,
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw getOperationAccessError(operationId);
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
